import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSetMemberDisplayNameMutation } from "@/store/rest_api";
import Membership from "@/model/interface/Membership";
import { apiErrorMessage } from "@/util/apiError";

const MAX_DISPLAY_NAME_LENGTH = 100;

interface DisplayNameDialogProps {
  member: Membership | null;
  onOpenChange: (open: boolean) => void;
}

export default function DisplayNameDialog({ member, onOpenChange }: DisplayNameDialogProps) {
  const [setMemberDisplayName, { isLoading }] = useSetMemberDisplayNameMutation();
  const [displayName, setDisplayName] = useState("");
  const realName = [member?.user.firstName, member?.user.lastName].filter(Boolean).join(" ");

  const save = async () => {
    if (!member) {
      return;
    }
    try {
      await setMemberDisplayName({ id: member.id, data: { displayName: displayName.trim() || null } }).unwrap();
      toast.success("Display name saved");
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save the display name"));
    }
  };

  useEffect(() => {
    setDisplayName(member?.displayName ?? "");
  }, [member]);

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          className="space-y-4"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();
            void save();
          }}
        >
          <DialogHeader>
            <DialogTitle>Display name</DialogTitle>
            <DialogDescription>
              How {realName || "this teammate"} shows up to your team in transfers, teams, calls and tasks. Leave it
              empty to use their name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="member-display-name">Display name</Label>
            <Input
              id="member-display-name"
              value={displayName}
              maxLength={MAX_DISPLAY_NAME_LENGTH}
              placeholder={realName}
              onChange={(changeEvent) => setDisplayName(changeEvent.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
